import {
  AttachmentType,
  BackendCommentsType,
  BackendFeedbackResponse,
  BackendTicketStatusType,
  CommentsType,
  FeatureStatus,
  FeedbackReponseType,
  JSONDocNode,
  JSONNode
} from '../types/support'
import { format } from 'date-fns'

export const defaultDocNode: JSONDocNode = {
  version: 1,
  type: 'doc',
  content: []
}

export const normalizeFeebackResponse = (featureResponse: BackendFeedbackResponse[]): FeedbackReponseType[] => {
  if (!featureResponse) {
    return []
  }
  return featureResponse.map((ticket: BackendFeedbackResponse) => {
    return {
      summary: ticket?.summary ?? '',
      issueId: ticket?.issue_id ?? '',
      issueLink: ticket?.issue_link ?? '',
      description: ticket?.description ?? defaultDocNode,
      currentResolution: ticket?.current_resolution ?? defaultDocNode,
      impactArea: ticket?.impact_area ?? defaultDocNode,
      suggestedSolve: ticket?.suggested_solve ?? defaultDocNode,
      status: {
        id: ticket?.status.id ?? '',
        name: STATUS_MAP[ticket?.status.name] ?? ''
      },
      createdOn: format(new Date(ticket?.created ?? ''), 'MM/dd/yyyy') ?? '',
      priority: ticket?.priority ?? 'Low',
      attachment: ticket?.attachment ?? [],
      files: [],
      comments: parseComments(ticket?.comments ?? []),
      // Change this based on backend key
      lastUpdated: format(new Date(ticket?.updated ?? ''), 'MM/dd/yyyy') ?? '',
      userDetails: ticket.user_details
    }
  })
}

const parseComments = (comments: BackendCommentsType[]): CommentsType[] => {
  return comments.map(comment => {
    return {
      text: comment.text,
      createdAt: format(new Date(comment?.created_at ?? ''), 'MM/dd/yyyy') ?? ''
    }
  })
}

export const groupFeedbackByStatus = (feedbackResponses: FeedbackReponseType[]) => {
  const defaultStatuses: FeatureStatus[] = ['todo', 'review', 'progress', 'completed', 'deferred']

  const groupedData = defaultStatuses.reduce((acc, statusName) => {
    acc[statusName] = {
      status: statusName,
      count: 0,
      data: [],
      isOpen: ['todo', 'progress', 'review'].includes(statusName)
    }
    return acc
  }, {} as Record<string, { status: FeatureStatus; count: number; data: FeedbackReponseType[]; isOpen: boolean }>)

  feedbackResponses.forEach(feedback => {
    const statusName = feedback.status.name

    if (!groupedData[statusName]) {
      groupedData[statusName] = {
        status: statusName,
        count: 0,
        data: [],
        isOpen: ['todo', 'progress', 'review'].includes(statusName)
      }
    }

    groupedData[statusName].count += 1
    groupedData[statusName].data.push(feedback)
  })

  return Object.values(groupedData)
}

export enum BackendTicketStatus {
  TO_DO = 'To Do',
  IN_PROGRESS = 'In Progress',
  IN_CONSIDERATION = 'In Consideration',
  IN_REVIEW = 'In Review',
  DONE = 'Done',
  DID_NOT_PICK = 'Did Not Pick'
}

export enum FeatureStatusEnum {
  TODO = 'todo',
  PROGRESS = 'progress',
  REVIEW = 'review',
  COMPLETED = 'completed',
  DEFERRED = 'deferred'
}

export const STATUS_MAP: Record<BackendTicketStatus, FeatureStatusEnum> = {
  [BackendTicketStatus.TO_DO]: FeatureStatusEnum.TODO,
  [BackendTicketStatus.IN_PROGRESS]: FeatureStatusEnum.PROGRESS,
  [BackendTicketStatus.IN_CONSIDERATION]: FeatureStatusEnum.REVIEW,
  [BackendTicketStatus.IN_REVIEW]: FeatureStatusEnum.REVIEW,
  [BackendTicketStatus.DONE]: FeatureStatusEnum.COMPLETED,
  [BackendTicketStatus.DID_NOT_PICK]: FeatureStatusEnum.DEFERRED
}

export const getMediaLinks = (attachment: AttachmentType[]): { fileUrl: string; fileName: string; id: number }[] => {
  return attachment.map((attachment: AttachmentType) => {
    return { fileUrl: attachment.content, fileName: attachment.filename, id: attachment.id }
  })
}

export const defaultFeatureSuggestionState: FeedbackReponseType = {
  summary: '',
  issueId: '',
  issueLink: '',
  description: defaultDocNode,
  currentResolution: defaultDocNode,
  impactArea: defaultDocNode,
  suggestedSolve: defaultDocNode,
  status: {
    id: '',
    name: ''
  },
  createdOn: '',
  priority: 'LOW',
  attachment: [],
  files: [],
  comments: [],
  lastUpdated: ''
}

const removeAttrsFields = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map(item => removeAttrsFields(item))
  }

  if (obj && typeof obj === 'object') {
    const newObj: any = {}
    for (const key in obj) {
      if (key !== 'attrs') {
        newObj[key] = removeAttrsFields(obj[key])
      }
    }
    return newObj
  }

  return obj
}

export const createTicketPayload = (data: FeedbackReponseType): FormData => {
  const formData = new FormData()

  //Might need to send user details here
  const requestPayload = {
    summary: data.summary,
    description: data.description,
    current_resolution: data.currentResolution,
    impact_area: data.impactArea,
    suggested_solve: data.suggestedSolve,
    priority: data.priority,
    user_details: data.userDetails
  }
  const attrsRemovedData = removeAttrsFields(requestPayload)
  formData.append('request', JSON.stringify(attrsRemovedData))

  data.files.forEach(file => {
    formData.append('files', file)
  })

  return formData
}

export const updateTicketPayload = (data: FeedbackReponseType): FormData => {
  const formData = new FormData()

  const requestPayload = {
    summary: data.summary,
    description: data.description,
    current_resolution: data.currentResolution,
    impact_area: data.impactArea,
    suggested_solve: data.suggestedSolve,
    priority: data.priority,
    attachments: data.attachment
  }

  const attrsRemovedData = removeAttrsFields(requestPayload)
  formData.append('request', JSON.stringify(attrsRemovedData))

  data.files.forEach(file => {
    formData.append('files', file)
  })

  return formData
}

const checkMinimumCharacter = (data: JSONDocNode) => {
  let text = ''
  const stack = [...data.content]

  while (stack.length) {
    const node = stack.pop()

    if (node?.type === 'paragraph' && Array.isArray(node.content)) {
      for (const item of node.content) {
        if (item?.type === 'text' && item.text) {
          text += item.text
        }
      }
    }
  }
  return !(text.length >= 50)
}

export const validateFeatureSuggestionForm = (
  featureSuggestionForm: FeedbackReponseType
): { hasError: boolean; errors: Record<string, string> } => {
  const errors: Record<string, string> = {}

  if (!featureSuggestionForm.summary?.trim()) {
    errors.summary = 'Feature name is required.'
  } else if (featureSuggestionForm.summary.length > 100) {
    errors.summary = 'Feature name cannot exceed 100 characters.'
  } else if (featureSuggestionForm.summary.length < 5) {
    errors.summary = 'Feature name cannot be less than 5 characters.'
  }
  if (checkMinimumCharacter(featureSuggestionForm.description)) {
    errors.description = 'Please enter a minimum of 50 characters.'
  }
  if (checkMinimumCharacter(featureSuggestionForm.impactArea)) {
    errors.impactArea = 'Please enter a minimum of 50 characters.'
  }
  if (checkMinimumCharacter(featureSuggestionForm.currentResolution)) {
    errors.currentResolution = 'Please enter a minimum of 50 characters.'
  }
  if (checkMinimumCharacter(featureSuggestionForm.suggestedSolve)) {
    errors.suggestedSolve = 'Please enter a minimum of 50 characters.'
  }
  if (!featureSuggestionForm.priority) {
    errors.priority = 'Priority is required.'
  }

  return { hasError: Object.keys(errors).length > 0, errors }
}

export const extractTextFromADF = (node: JSONNode | undefined): string[] => {
  if (!node) return []

  let texts: string[] = []

  if (typeof node.text === 'string') {
    texts.push(node.text)
  }

  if (Array.isArray(node.content)) {
    node.content.forEach(child => {
      texts = texts.concat(extractTextFromADF(child))
    })
  }

  return texts
}
